import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {Hotel} from '@findhotel/sapi/dist/types/packages/core/src/types/types'
import {getLocaleCode} from 'modules/meta/selectors'
import {useRouter} from 'next/router'

import {Box} from '@vio/atlas/helpers/Box'
import {
  Accordion,
  Header,
  HeaderText
} from '@vio/atlas/src/components/designSystem/Accordion'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import {cssTheme} from '@vio/atlas/src/themes'
import minus from '@vio/atlas/src/utils/minus'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {HotelTopAttributes} from '../HotelTopAttributes/HotelTopAttributes'

interface Props {
  hotel: Hotel
  fullwidth?: boolean
}

const StyledAccordion = styled(Accordion)`
  &.--non-fullwidth {
    margin-top: ${minus(cssTheme.layout.spacing.s700)};
    ${Header} {
      width: 50%;
      margin-left: auto;
      justify-content: flex-end;
    }

    ${HeaderText} {
      color: ${cssTheme.colors.content.neutral.secondary};
    }
  }
  &.--fullwidth {
    ${Header} {
      width: 100%;
      justify-content: space-between;
    }
  }
`

const getDescriptionString = (hotel: Hotel, hasFilters: boolean) => {
  if (!hotel.descriptions) return null
  const {amenities, headline} = hotel.descriptions

  let description = headline
  if (hasFilters) {
    description = amenities ?? headline
  }

  return description.length > 200
    ? description.slice(0, 200) + '...'
    : description
}

export const HotelMoreDetails = ({hotel, fullwidth = false}: Props) => {
  const {query} = useRouter()
  const locale = useSelector(getLocaleCode)
  const hasFilters = query.taxonomy?.includes('f')

  const description = getDescriptionString(hotel, hasFilters)

  const hotelTopAttributes = <HotelTopAttributes hotel={hotel} />

  if (!description && !hotelTopAttributes) return null

  // Hotel description are only available in English
  if (!locale.startsWith('en')) return null

  return (
    <>
      <Box
        padding={fullwidth ? 's400' : 's000'}
        paddingTop={fullwidth ? 's200' : 's000'}
        paddingBottom={fullwidth ? 's200' : 's000'}
      >
        <StyledAccordion
          isOpenByDefault={process.env.STORYBOOK === 'true'}
          header={
            <FormattedMessageWrapper id="readMore" defaultMessage="Read more" />
          }
          onClick={e => {
            e.stopPropagation()
          }}
          size="sm"
          className={cx(
            fullwidth && '--fullwidth',
            !fullwidth && '--non-fullwidth'
          )}
        >
          <Box
            padding={fullwidth ? 's000' : 's200'}
            paddingBottom={fullwidth ? 's400' : 's200'}
            display="flex"
            flexDirection="column"
            gap="s400"
          >
            {description && <Text variant="bodyS">{description}</Text>}
            {hotelTopAttributes}
          </Box>
        </StyledAccordion>
      </Box>
    </>
  )
}
